import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { 
  HomeIcon,
  DocumentTextIcon,
  CogIcon,
  NewspaperIcon,
  KeyIcon,
  UserGroupIcon,
  Cog8ToothIcon,
  XMarkIcon,
  RssIcon,
  DocumentIcon,
  UserCircleIcon,
  MegaphoneIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { 
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  CogIcon as CogIconSolid,
  NewspaperIcon as NewspaperIconSolid,
  KeyIcon as KeyIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  Cog8ToothIcon as Cog8ToothIconSolid,
  RssIcon as RssIconSolid,
  DocumentIcon as DocumentIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  MegaphoneIcon as MegaphoneIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';

interface NavigationItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  iconSolid: React.ElementType;
  badge?: string;
  adminOnly?: boolean;
  children?: NavigationItem[];
}

const navigation: NavigationItem[] = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: HomeIcon, 
    iconSolid: HomeIconSolid 
  },
  { 
    name: 'Prompts', 
    href: '/prompts', 
    icon: DocumentTextIcon, 
    iconSolid: DocumentTextIconSolid 
  },
  { 
    name: 'Markdowns', 
    href: '/md-files', 
    icon: DocumentIcon, 
    iconSolid: DocumentIconSolid
  },
  { 
    name: 'Workflows', 
    href: '/workflows', 
    icon: CogIcon, 
    iconSolid: CogIconSolid 
  },
  { 
    name: 'AI News', 
    href: '/news', 
    icon: NewspaperIcon, 
    iconSolid: NewspaperIconSolid 
  },
  {
    name: 'Community',
    icon: UserCircleIcon,
    iconSolid: UserCircleIconSolid,
    children: [
      {
        name: 'Notice',
        href: '/community/notice',
        icon: MegaphoneIcon,
        iconSolid: MegaphoneIconSolid
      },
      {
        name: 'Free',
        href: '/community/free',
        icon: ChatBubbleLeftRightIcon,
        iconSolid: ChatBubbleLeftRightIconSolid
      }
    ]
  },
  { 
    name: 'User Management', 
    href: '/admin/users', 
    icon: UserGroupIcon, 
    iconSolid: UserGroupIconSolid,
    adminOnly: true,
  },
  { 
    name: 'News Crawler', 
    href: '/admin/news-crawler', 
    icon: RssIcon, 
    iconSolid: RssIconSolid,
    adminOnly: true,
  },
  { 
    name: 'System Settings', 
    href: '/admin/settings', 
    icon: Cog8ToothIcon, 
    iconSolid: Cog8ToothIconSolid,
    adminOnly: true,
  },
];

interface SidebarProps {
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const [expandedItems, setExpandedItems] = React.useState<string[]>(['Community']);

  // Filter navigation items based on user role
  const filteredNavigation = navigation.filter(item => {
    if (item.adminOnly && !isAdmin) {
      return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col w-64 h-screen bg-white dark:bg-dark-sidebar border-r border-gray-200 dark:border-dark-border">
      {/* Logo Header - 헤더와 높이 맞춤 */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-dark-border md:justify-start">
        <Link 
          to="/" 
          onClick={() => onClose?.()} // Close mobile sidebar when logo is clicked
          className="flex items-center space-x-2"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">P</span>
          </div>
          <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">PromptHub</span>
        </Link>
        
        {/* Close button for mobile */}
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:text-dark-text-primary dark:hover:bg-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 md:hidden"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {filteredNavigation.map((item) => {
          // Handle items with children (Community menu)
          if (item.children) {
            const isExpanded = expandedItems.includes(item.name);
            return (
              <div key={item.name}>
                <button
                  onClick={() => {
                    setExpandedItems((prev) =>
                      isExpanded
                        ? prev.filter((name) => name !== item.name)
                        : [...prev, item.name]
                    );
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-card hover:text-gray-900 dark:hover:text-dark-text-primary"
                >
                  <div className="flex items-center">
                    <item.icon className="w-5 h-5 mr-3" />
                    <span>{item.name}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4" />
                  )}
                </button>
                {isExpanded && (
                  <div className="mt-1 ml-6 space-y-1">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.name}
                        to={child.href!}
                        onClick={() => onClose?.()}
                        className={({ isActive }) =>
                          clsx(
                            'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                            {
                              'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400': isActive,
                              'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-card hover:text-gray-900 dark:hover:text-dark-text-primary': !isActive,
                            }
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive ? (
                              <child.iconSolid className="w-4 h-4 mr-3" />
                            ) : (
                              <child.icon className="w-4 h-4 mr-3" />
                            )}
                            <span>{child.name}</span>
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Handle regular items
          return (
            <NavLink
              key={item.name}
              to={item.href!}
              onClick={() => onClose?.()} // Close mobile sidebar when navigation item is clicked
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                  {
                    'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-500': isActive,
                    'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-card hover:text-gray-900 dark:hover:text-dark-text-primary': !isActive,
                  }
                )
              }
            >
              {({ isActive }) => {
              const IconComponent = isActive ? item.iconSolid : item.icon;
              return (
                <>
                  <IconComponent className="w-5 h-5 mr-3" />
                  {item.name}
                  {item.badge && (
                    <span className="ml-auto bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-medium px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </>
              );
            }}
            </NavLink>
          );
        })}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-gray-200 dark:border-dark-border">
        <div className="flex items-center space-x-3">
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 bg-gray-300 dark:bg-dark-badge-gray rounded-full flex items-center justify-center">
              <span className="text-gray-600 dark:text-dark-text-primary text-sm font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-dark-text-secondary truncate">
              {user?.email}
            </p>
          </div>
        </div>
        
        {/* Quick settings */}
        <div className="mt-3">
          <Link
            to="/settings"
            onClick={() => onClose?.()} // Close mobile sidebar when settings is clicked
            className="flex items-center justify-center w-full px-3 py-2 text-sm text-gray-600 dark:text-dark-text-secondary bg-gray-50 dark:bg-dark-card rounded-lg hover:bg-gray-100 dark:hover:bg-dark-badge-gray transition-colors duration-200"
          >
            <Cog8ToothIcon className="w-4 h-4 mr-2" />
            Settings
          </Link>
        </div>
      </div>
    </div>
  );
};